import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const IMAGE_BASE_URL_LARGE = 'https://media.server.theshootingwarehouse.com/large/';
const IMAGE_BASE_URL_HIRES = 'https://media.server.theshootingwarehouse.com/hires/';
const SPEC_LINE_COLOR = '#94a3b8';
const labelSx = { fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 };
const valueSx = { fontWeight: 600, color: 'text.primary' };

const SpecRow = ({ label, value }) => (
  <Grid container spacing={1} sx={{ py: 0.5 }}>
    <Grid item xs={4} sm={3}>
      <Typography variant='body2' sx={labelSx}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={8} sm={9}>
      <Typography variant='body2' sx={valueSx}>
        {value || '\u2014'}
      </Typography>
    </Grid>
  </Grid>
);

const NotesLines = ({ lines = 5 }) => (
  <Stack spacing={1.2} sx={{ mt: 1 }}>
    {Array.from({ length: lines }).map((_, idx) => (
      <Box key={idx} sx={{ borderBottom: '1.5px solid', borderColor: SPEC_LINE_COLOR, minHeight: 18 }} />
    ))}
  </Stack>
);

const waitForNextPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

const waitForImageToRender = async (img) => {
  if (!img) return;

  if (!img.complete || !img.naturalWidth) {
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
  }

  if (img.decode) {
    try {
      await img.decode();
    } catch (e) {
      // Some browsers reject decode() for cross-origin images that still render correctly.
    }
  }

  await waitForNextPaint();
};

const ItemSpecSheet = () => {
  const { itemNumber } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [hasImage, setHasImage] = useState(true);
  const [imageSrc, setImageSrc] = useState('');
  const [imageReady, setImageReady] = useState(true);
  const [hiddenImageReady, setHiddenImageReady] = useState(true);
  const [triedHires, setTriedHires] = useState(false);
  const contentRef = useRef(null);
  const captureImageRef = useRef(null);

  const printedOn = useMemo(
    () =>
      new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    []
  );

  const facets = useMemo(() => {
    if (!item?.facets) return [];
    try {
      const parsed = JSON.parse(item.facets);
      return Object.entries(parsed).map(([key, val]) => ({
        key,
        value: val,
      }));
    } catch (e) {
      return [];
    }
  }, [item?.facets]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setHasImage(true);
    setImageSrc('');
    setImageReady(false);
    setHiddenImageReady(false);
    setGeneratingPdf(false);
    setTriedHires(false);

    axios
      .get(`/itemSpecSheet/${itemNumber}`)
      .then(({ data }) => {
        if (!active) return;
        const record = Array.isArray(data) ? data[0] : data;
        const hasContent = record && Object.keys(record).length > 0;
        setItem(hasContent ? record : null);
        setNotFound(!hasContent);
      })
      .catch(() => {
        if (!active) return;
        setError('Unable to load item spec sheet.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [itemNumber]);

  const headline = item?.description || 'Item Specification Sheet';
  const itemShouldHaveImage = !!item?.itemno;
  const pdfReady = !!item && (!itemShouldHaveImage || !hasImage || (!!imageSrc && imageReady && hiddenImageReady));

  useEffect(() => {
    if (item?.itemno) {
      setHasImage(true);
      setImageReady(false);
      setHiddenImageReady(false);
      setTriedHires(false);
      setImageSrc(`${IMAGE_BASE_URL_LARGE}${item.itemno}.jpg`);
    } else {
      setHasImage(false);
      setImageReady(true);
      setHiddenImageReady(true);
      setImageSrc('');
    }
  }, [item?.itemno]);

  const handleImageLoad = () => {
    setHasImage(true);
    setImageReady(true);
  };

  const handleCaptureImageLoad = async (event) => {
    try {
      await waitForImageToRender(event.currentTarget);
      setHiddenImageReady(true);
    } catch (e) {
      handleImageError();
    }
  };

  const handleImageError = () => {
    if (!triedHires && item?.itemno) {
      setTriedHires(true);
      setImageReady(false);
      setHiddenImageReady(false);
      setImageSrc(`${IMAGE_BASE_URL_HIRES}${item.itemno}.png`);
    } else {
      setHasImage(false);
      setImageReady(true);
      setHiddenImageReady(true);
      setImageSrc('');
    }
  };

  const generatePdf = useCallback(async () => {
    if (!item || !contentRef.current) return;
    if (!pdfReady || generatingPdf) return;

    setGeneratingPdf(true);
    try {
      if (imageSrc && hasImage) {
        await waitForImageToRender(captureImageRef.current);
      } else {
        await waitForNextPaint();
      }

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'letter');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      let pdfWidth = pageWidth;
      let pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      const topMargin = 20;
      const availableHeight = pageHeight - topMargin;

      if (pdfHeight > availableHeight) {
        const scale = availableHeight / pdfHeight;
        pdfHeight = pageHeight;
        pdfWidth = pdfWidth * scale;
      }

      const marginX = (pageWidth - pdfWidth) / 2;
      const marginY = topMargin;

      pdf.addImage(imgData, 'PNG', marginX, marginY, pdfWidth, pdfHeight, undefined, 'FAST');

      pdf.output('dataurlnewwindow');
    } catch (e) {
      console.error('Unable to generate item spec sheet PDF.', e);
    } finally {
      setGeneratingPdf(false);
    }
  }, [generatingPdf, hasImage, imageSrc, item, pdfReady]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f3f5f8',
        py: { xs: 3, md: 6 },
        px: { xs: 1.5, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        {loading && (
          <Stack alignItems='center' spacing={2} sx={{ py: 8 }}>
            <CircularProgress />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Loading item data...
            </Typography>
          </Stack>
        )}

        {!loading && error && <Alert severity='error'>{error}</Alert>}

        {!loading && notFound && <Alert severity='error'>Invalid Product Number: {itemNumber}</Alert>}

        {!loading && !error && item && (
          <Paper
            elevation={0}
            sx={{
              overflow: 'hidden',
              border: '1px solid #d7dde7',
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, md: 4 },
                py: { xs: 2.5, md: 3 },
                borderBottom: '1px solid #d7dde7',
                backgroundColor: '#182433',
                color: '#fff',
              }}
            >
              <Stack spacing={1}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Inventory2Icon fontSize='small' />
                  <Typography variant='overline' sx={{ color: '#cbd5e1', letterSpacing: 1 }}>
                    Item Spec Sheet
                  </Typography>
                </Stack>
                <Typography variant='h5' sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                  {headline}
                </Typography>
              </Stack>
            </Box>

            <Grid container>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    minHeight: { xs: 280, md: 420 },
                    p: { xs: 2.5, md: 4 },
                    borderRight: { md: '1px solid #d7dde7' },
                    borderBottom: { xs: '1px solid #d7dde7', md: 'none' },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#fafbfc',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: 250,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: '1px solid #d7dde7',
                      borderRadius: 2,
                      backgroundColor: '#fff',
                      p: 2,
                    }}
                  >
                    {imageSrc && hasImage ? (
                      <Box
                        component='img'
                        src={imageSrc}
                        alt={headline}
                        sx={{ maxHeight: 260, maxWidth: '100%', objectFit: 'contain' }}
                        crossOrigin='anonymous'
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    ) : (
                      <Stack alignItems='center' spacing={1.5} sx={{ color: 'text.secondary' }}>
                        <ImageSearchIcon />
                        <Typography variant='body2'>
                          {imageReady ? 'Image not available' : 'Loading image...'}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Stack
                  spacing={3}
                  justifyContent='center'
                  sx={{ p: { xs: 2.5, md: 4 }, minHeight: { md: 420 } }}
                >
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid #d7dde7',
                      borderRadius: 2,
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                      <Stack spacing={0.25} sx={{ flex: 1 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 800 }}>
                          PDF output
                        </Typography>
                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                          {pdfReady
                            ? 'Ready to create the customer spec sheet.'
                            : 'Waiting for the product image to finish loading.'}
                        </Typography>
                      </Stack>
                      <Button
                        variant='contained'
                        size='large'
                        startIcon={<PictureAsPdfIcon />}
                        onClick={generatePdf}
                        disabled={generatingPdf || !pdfReady}
                        sx={{
                          minWidth: 190,
                          py: 1.25,
                          fontWeight: 800,
                          bgcolor: '#1d4ed8',
                          boxShadow: '0 10px 20px rgba(29, 78, 216, 0.22)',
                          '&:hover': { bgcolor: '#1e40af' },
                        }}
                      >
                        {generatingPdf ? 'Opening...' : pdfReady ? 'Open Spec Sheet' : 'Loading Image'}
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>

      {/* Hidden render target for html2canvas to capture */}
      <Box
        sx={{
          position: 'absolute',
          left: '-10000px',
          top: 0,
          width: '100%',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
          <Paper
            elevation={4}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 2,
              background: '#fff',
              boxShadow:
                '0 10px 30px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)',
            }}
            ref={contentRef}
          >
            {!loading && !error && item && (
              <Stack spacing={3}>
                <Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='space-between'>
                    <Stack spacing={0.5}>
                      <Typography variant='overline' sx={{ color: 'text.secondary' }}>
                        Vendor Name
                      </Typography>
                      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                        Sports South, LLC
                      </Typography>
                    </Stack>
                    <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                      <Typography variant='overline' sx={{ color: 'text.secondary' }}>
                        Printed on
                      </Typography>
                      <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                        {printedOn}
                      </Typography>
                    </Stack>
                </Stack>
              </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: SPEC_LINE_COLOR,
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#fff',
                    minHeight: 240,
                  }}
                >
                  {imageSrc && hasImage ? (
                    <Box
                      component='img'
                      ref={captureImageRef}
                      src={imageSrc}
                      alt={headline}
                      crossOrigin='anonymous'
                      sx={{ maxHeight: 240, maxWidth: '100%', objectFit: 'contain' }}
                      onLoad={handleCaptureImageLoad}
                      onError={handleImageError}
                    />
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Image not available
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: SPEC_LINE_COLOR }} />

                <Stack spacing={1}>
                  <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    Item Description
                  </Typography>
                  <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1.4 }}>
                    {headline}
                  </Typography>
                </Stack>

                <Divider sx={{ borderColor: SPEC_LINE_COLOR }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SpecRow label='Item Number' value={item?.itemno} />
                    <SpecRow label='UPC' value={item?.upc} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SpecRow label='Manufacturer SKU' value={item?.mfgino} />
                    <SpecRow label='Brand' value={item?.brand_name} />
                  </Grid>
                </Grid>

                {!!facets.length && (
                  <Stack spacing={1.5}>
                    <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Specs / Attributes
                    </Typography>
                    <Grid container spacing={2}>
                      {facets.map(({ key, value }) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <SpecRow label={key} value={value} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                )}

                <Stack spacing={1}>
                  <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    Quoted Price
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems='center'>
                    <Box sx={{ flex: 1, borderBottom: '2px solid', borderColor: SPEC_LINE_COLOR, minHeight: 28 }} />
                    <Typography variant='body2' sx={{ color: 'text.secondary', px: 1 }}>
                      Clerk Initials
                    </Typography>
                    <Box sx={{ width: 120, borderBottom: '2px solid', borderColor: SPEC_LINE_COLOR, minHeight: 28 }} />
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                    Notes
                  </Typography>
                  <NotesLines lines={7} />
                </Stack>
              </Stack>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ItemSpecSheet;
